import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { MatSnackBar } from '@angular/material/snack-bar';
import { TranslateService } from '@ngx-translate/core';
import { lastValueFrom } from 'rxjs';
import { AuthService } from 'src/app/auth/services/auth.service';
import constants from 'src/app/shared/constants';
import { UserCompanyModel } from 'src/app/shared/models/user-company-model';
import { environment } from 'src/environments/environment';
import { CompanyType } from '../constants';
import { EventService } from 'src/app/panel/services/event.service';

@Injectable({
  providedIn: 'root',
})
export class CompanyUserService {
  uriCompany = `${environment.webappEndpoint}/company`;
  uriUserCompany = `${environment.webappEndpoint}/userCompany`;
  companyIdChosenByAdmin: string;

  constructor(
    private readonly http: HttpClient,
    private readonly auth: AuthService,
    private readonly snackBar: MatSnackBar,
    private readonly translateService: TranslateService,
    private readonly eventService: EventService,
  ) {
    this.companyIdChosenByAdmin = localStorage.getItem(constants.companyIdChosenByAdmin);
  }

  adminDidNotChooseCompany() {
    this.companyIdChosenByAdmin = localStorage.getItem(constants.companyIdChosenByAdmin);
    return this.auth.hasRoleAdmin() && !this.companyIdChosenByAdmin;
  }

  adminHasChosenCompany() {
    this.companyIdChosenByAdmin = localStorage.getItem(constants.companyIdChosenByAdmin);
    return this.auth.hasRoleAdmin() && this.companyIdChosenByAdmin;
  }

  unsetCompanyIdChosenByAdmin(openPopup = false) {
    if (!this.auth.hasRoleAdmin()) return;

    this.companyIdChosenByAdmin = null;
    localStorage.removeItem(constants.companyIdChosenByAdmin);

    this.eventService.adminChooseCompanyEvent.emit({ openPopup });
  }

  setCompanyIdChosenByAdmin(companyId: string) {
    if (!this.auth.hasRoleAdmin()) return;

    this.companyIdChosenByAdmin = companyId;
    localStorage.setItem(constants.companyIdChosenByAdmin, companyId);

    this.eventService.adminChooseCompanyEvent.emit({ openPopup: false });
  }

  getCompanyIdChosenByAdmin() {
    if (!this.auth.hasRoleAdmin()) return null;

    this.companyIdChosenByAdmin = localStorage.getItem(constants.companyIdChosenByAdmin);
    return this.companyIdChosenByAdmin;
  }

  httpRequest(method, param, callback, callbackParams, errorMessage) {
    method(...param).subscribe({
      next: (res: any) => {
        callback(res, callbackParams);
        if (res.reason) {
          this.showSnackBarByDirectMessage(res.reason);
        }
      },
      error: (error) => {
        callback({ success: false });
        if (error?.status === 401) {
          this.showSnackBarByTranslationKey('Shared.SessionExpired');
        } else if (error.error?.reason) {
          this.showSnackBarByDirectMessage(error.error.reason, true);
        } else {
          this.showSnackBarByDirectMessage(errorMessage, true);
        }
      },
    });
  }

  showSnackBarByDirectMessage = async (message: string, error = false) => {
    if (error) message = `${this.translateService.instant('Shared.Error')}: ${message}`;

    this.snackBar.open(message, await lastValueFrom(this.translateService.get('Shared.Close')), {
      duration: constants.snackBarDuration,
    });
  };

  showSnackBarByTranslationKey = async (translationKey: string) => {
    this.snackBar.open(
      await lastValueFrom(this.translateService.get(translationKey)),
      await lastValueFrom(this.translateService.get('Shared.Close')),
      { duration: constants.snackBarDuration },
    );
  };

  getCompanies = () =>
    this.http.get(`${this.uriCompany}/getCompanies`, { headers: { Authorization: `Bearer ${this.auth.getToken()}` } });
  getCompany = (_id: string) =>
    this.http.get(`${this.uriCompany}/getCompany/${_id}`, {
      headers: { Authorization: `Bearer ${this.auth.getToken()}` },
    });
  createCompany = (company: CompanyType) =>
    this.http.post(`${this.uriCompany}/create`, company, {
      headers: { Authorization: `Bearer ${this.auth.getToken()}` },
    });
  updateCompany = (company: CompanyType) =>
    this.http.post(`${this.uriCompany}/update`, company, {
      headers: { Authorization: `Bearer ${this.auth.getToken()}` },
    });
  updateKaironConfig = (company: CompanyType) =>
    this.http.post(`${this.uriCompany}/updateKaironConfig`, company, {
      headers: { Authorization: `Bearer ${this.auth.getToken()}` },
    });
  deleteCompany = (_id: string) =>
    this.http.post(
      `${this.uriCompany}/delete`,
      { _id },
      { headers: { Authorization: `Bearer ${this.auth.getToken()}` } },
    );

  getAllCompanyNamesAndIds = () =>
    this.http.get(`${this.uriCompany}/getAllCompanyNamesAndIds`, {
      headers: { Authorization: `Bearer ${this.auth.getToken()}` },
    });
  createUser = (user: UserCompanyModel) =>
    this.http.post(`${this.uriUserCompany}/create`, user, {
      headers: { Authorization: `Bearer ${this.auth.getToken()}` },
    });
  updateUser = (user: UserCompanyModel) =>
    this.http.post(`${this.uriUserCompany}/update/${user._id}`, user, {
      headers: { Authorization: `Bearer ${this.auth.getToken()}` },
    });
  deleteUser = (_id: string) =>
    this.http.post(
      `${this.uriUserCompany}/delete`,
      { _id },
      { headers: { Authorization: `Bearer ${this.auth.getToken()}` } },
    );
  updateRole = (_id: string, role: string, companyID: string, companyName: string) =>
    this.http.post(
      `${this.uriUserCompany}/updateRole`,
      { _id, role, companyID, companyName },
      { headers: { Authorization: `Bearer ${this.auth.getToken()}` } },
    );
  createReceiver = (companyId: string) =>
    this.http.post(
      `${this.uriCompany}/createReceiver`,
      { companyId },
      {
        headers: { Authorization: `Bearer ${this.auth.getToken()}` },
      },
    );
  getReceiverServers = () =>
    this.http.get(`${this.uriCompany}/receiver/servers`, {
      headers: { Authorization: `Bearer ${this.auth.getToken()}` },
    });

  validateEmail = async (email: string, _id = null) => {
    return this.http.post(
      `${this.uriUserCompany}/validateEmail`,
      { email, _id },
      { headers: { Authorization: `Bearer ${this.auth.getToken()}` } },
    );
  };

  uriPartition = `${environment.webappEndpoint}/partition`;

  getPartitionsForSimulation = () =>
    this.http.get(`${this.uriPartition}/getForSimulation`, {
      headers: { Authorization: `Bearer ${this.auth.getToken()}` },
    });

  simulateVoiceCall = (partitionId: string, phone: string, safeWord?: string) =>
    this.http.post(
      `${this.uriPartition}/simulateVoiceCall`,
      { partitionId, phone, safeWord },
      { headers: { Authorization: `Bearer ${this.auth.getToken()}` } },
    );
}
